import { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download } from 'lucide-react';
import MedicationAdminNote from '../components/MedicationAdminNote';
import { useSession } from '../contexts/SessionContext';
import { generatePDF } from '../services/pdfGenerator';
import { ROUTES } from '../constants/routes';
import { getNoteDetailRoute } from '../utils/noteDetailRoutes';
import NoteTimingMeta from '../components/NoteTimingMeta';
import NoteAddendumsList from '../components/NoteAddendumsList';
import { resolveNoteTiming } from '../utils/lateEntry';
import { getNoteAddendums } from '../utils/addendum';

export default function MedicationDetailScreen() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { sessionNotes } = useSession();
  const note = location.state?.note
    ? sessionNotes.find((n) => n.id === location.state.note.id) || location.state.note
    : sessionNotes.find((n) => n.id === id);
  const content = note?.openaiNote || note?.note_content;
  const datetime = note ? new Date(note.datetime) : new Date();
  const timing = note ? resolveNoteTiming(note) : { careTime: null, isLateEntry: false };

  useEffect(() => {
    if (!note) return;
    if (note.note_type === 'medication_admin') return;
    const redirect = getNoteDetailRoute(note);
    if (redirect) {
      navigate(redirect.path, { replace: true, state: redirect.state });
      return;
    }
    navigate(ROUTES.MEDICATIONS, { replace: true });
  }, [navigate, note]);

  if (!note || note.note_type !== 'medication_admin' || !content) {
    return <div className="p-8 text-center text-gray-500">Medication record not found</div>;
  }

  const handlePdf = () => {
    generatePDF({
      note: content,
      noteType: 'medication_admin',
      patientName: note.patient_name || 'Unknown',
      room: note.patient_room || '—',
      datetime,
      careTime: timing.careTime,
      isLateEntry: timing.isLateEntry,
      addendums: getNoteAddendums(note),
    });
  };

  return (
    <div className="min-h-screen bg-page pb-28">
      <header className="sticky top-0 border-b border-gray-200 bg-surface px-4 py-3">
        <button
          type="button"
          onClick={() => navigate(ROUTES.MEDICATIONS)}
          className="flex items-center gap-2 text-primary"
        >
          <ArrowLeft className="h-5 w-5" />
          Back
        </button>
        <h1 className="mt-2 text-lg font-bold">{note.patient_name}</h1>
        <NoteTimingMeta note={note} className="mt-1" />
      </header>
      <div className="px-4 py-4">
        <div className="pointer-events-none">
          <MedicationAdminNote note={content} onChange={() => {}} />
        </div>
        <NoteAddendumsList note={note} />
      </div>

      <div className="fixed bottom-0 left-0 right-0 flex items-center justify-center border-t border-gray-200 bg-surface p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
        <button
          type="button"
          onClick={handlePdf}
          className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-white"
          aria-label="Download PDF"
        >
          <Download className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
